import {
  APPROVAL_REAUTH_DEFAULT,
  type ApprovalReauthPolicy,
  isReauthRequired,
  type ReauthKind,
} from '@felix/domain';
import { PasswordInput } from '@mantine/core';
import { createContext, type ReactNode, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './approval-policy.module.css';

/**
 * Şirketin onay ayarı (ADR 0056 §4): uygulama oturum profilinden verir (gemide `auth/me`, ofiste
 * `office/auth/me`). Verilmezse varsayılan (kapalı); sunucu yine de parola isterse pencere alanı
 * açar (`useReauth`).
 */
export const ApprovalPolicyContext = createContext<ApprovalReauthPolicy>(APPROVAL_REAUTH_DEFAULT);

/** Sunucu parola istedi mi (`REAUTHENTICATION_REQUIRED`). */
export function isReauthError(error: unknown): boolean {
  return (error as { code?: unknown } | null)?.code === 'REAUTHENTICATION_REQUIRED';
}

/**
 * Parola alanı görünür mü: tür verilmeyen pencere (kritik işlem) her zaman parolalıdır; türün
 * kuralı (`REAUTH_POLICY`) ve şirket ayarı; sunucu istediyse (`forced`) her durumda.
 */
export function passwordShown(
  kind: ReauthKind | undefined,
  policy: ApprovalReauthPolicy,
  forced: boolean,
): boolean {
  return kind === undefined || forced || isReauthRequired(kind, policy);
}

export interface Reauth {
  /** Parola alanı görünür. */
  shown: boolean;
  password: string;
  setPassword: (value: string) => void;
  /** Alan görünür ama boş: onay düğmesi pasif. */
  missing: boolean;
  /** İsteğe eklenecek parola: yalnız girildiyse (boş parola gönderilmez). */
  body: () => { password?: string };
  /** Sunucu parola isterse (bayat profil) alan açılır. */
  noteError: (error: unknown) => void;
  reset: () => void;
}

/**
 * Onay penceresinin parola durumu (ADR 0056 §3): parolasız türde alan görünmez, açık onay adımı
 * (düğme) yeter. Parola verilirse sunucu doğrular ve kayıt `reauthenticated: true` olur.
 */
export function useReauth(kind?: ReauthKind): Reauth {
  const policy = useContext(ApprovalPolicyContext);
  const [forced, setForced] = useState(false);
  const [password, setPassword] = useState('');
  const shown = passwordShown(kind, policy, forced);
  const noteError = useCallback((error: unknown) => {
    if (isReauthError(error)) setForced(true);
  }, []);
  const reset = useCallback(() => setPassword(''), []);
  return {
    shown,
    password,
    setPassword,
    missing: shown && password === '',
    body: () => (shown && password !== '' ? { password } : {}),
    noteError,
    reset,
  };
}

export interface ReauthFieldProps {
  reauth: Reauth;
  label: ReactNode;
  description?: ReactNode;
  mb?: string;
  /**
   * Parolasız pencerenin bilgi metni; verilmezse onay metni. Onay olmayan işlem (iptal) kendi
   * metnini verir: "onaylıyorsunuz" yazılmaz [sorular #523].
   */
  note?: ReactNode;
}

/** Parola alanı ya da parolasız onayın bilgi metni. */
export function ReauthField({ reauth, label, description, mb = 'sm', note }: ReauthFieldProps) {
  const { t } = useTranslation();
  if (!reauth.shown) {
    return (
      <p className={classes.note} role="note">
        {note ?? t('approvalPolicy.note')}
      </p>
    );
  }
  return (
    <PasswordInput
      label={label}
      {...(description ? { description } : {})}
      required
      autoComplete="current-password"
      value={reauth.password}
      onChange={(event) => reauth.setPassword(event.currentTarget.value)}
      mb={mb}
    />
  );
}
